import { NO_INVOICE_FILTER_ID } from 'containers/jobs/constant';

export enum InvoiceVersion {
  THREE_POINT_ZERO = '3.0',
  THREE_POINT_ONE = '3.1'
}

export enum InvoiceSourceEnum {
  WEB = 'Web',
  MOBILE = 'Mobile'
}

export enum InvoicePanelPages {
  DETAILS = 'details',
  TRANSACTIONS = 'transactions'
}

export enum InvoiceTimelineCTAActions {
  SHARE_INVOICE = 'share-invoice'
}

export const INVOICE_STATUS_FILTER = [
  {
    label: 'No invoice',
    value: NO_INVOICE_FILTER_ID
  },
  {
    label: 'Paid',
    value: 'PAID-QR,PAID'
  },
  {
    label: 'Pending',
    value: 'PENDING,PROCESSING'
  }
];

import { ChargeCardErrorState, ChargeCardSuccessState } from './types';
import { InvoiceStatus } from './invoices.model';
import { FilterDropdownType, FilterOption } from 'components/more-filters/MoreFilters';
import { FilterSelectionType } from 'constants/common';

export const initialChargeCardErrorState: ChargeCardErrorState = {
  isOpen: false,
  message: 'Payment Failed',
  subtitle: 'Something went wrong',
  txnId: '',
  cardLast4: '',
  network: '',
  canRetry: true
};

export const initialChargeCardSuccessState: ChargeCardSuccessState = {
  cardData: null,
  transactionId: '',
  isOpen: false
};

export const CARD_ON_FILE_MESSAGES = {
  NOT_ADMIN_ENABLED: 'Card on file is not enabled on your account yet. Please contact our support team to activate it.',
  TEAM_MEMBER: 'Card on file is not enabled for your team yet. Please contact your team owner/admin to enable it',
  TEAM_OWNER_ADMIN: 'Card on file is not enabled for your team yet. Please enable it from team settings'
};

export const MANUAL_KEY_IN_MESSAGES = {
  NOT_ADMIN_ENABLED:
    'Manual Card Entry is not enabled on your account yet. Please contact our support team to activate it.',
  TEAM_MEMBER: 'Manual Card Entry is not enabled for your team yet. Please contact your team owner/admin to enable it',
  TEAM_OWNER_ADMIN: 'Manual Card Entry is not enabled for your team yet. Please enable it from team settings'
};

export enum TipTimingEnum {
  ALL_PAYMENTS = 'ON_ALL_PAYMENTS',
  FINAL_PAYMENT_ONLY = 'ON_FINAL_PAYMENT_ONLY'
}

export const TIP_TIMING_OPTIONS = [
  { id: TipTimingEnum.ALL_PAYMENTS, label: 'On all payments' },
  { id: TipTimingEnum.FINAL_PAYMENT_ONLY, label: 'On final payment only' }
];

export enum ShareViaType {
  SMS = 'SMS',
  EMAIL = 'EMAIL'
}

export enum InvoiceFilterIds {
  CUSTOMERS = 'customers',
  DATE_RANGE = 'dateRange',
  CREATED_BY = 'createdBy',
  DUE_DATE = 'dueDate',
  AMOUNT = 'amount',
  STATUS = 'status',
  REFUND_STATUS = 'refundStatus',
  DISPUTE_STATUS = 'disputeStatus',
  REMINDER_STATUS = 'reminderStatus',
  JOB_STATUS = 'jobStatus',
  ESTIMATE_STATUS = 'estimateStatus',
  TIP_COLLECTED = 'tipCollected',
  PAYMENT_METHODS = 'paymentMethods'
}

export const REFUND_STATUS_OPTIONS = [
  { id: 'NO_REFUNDS', label: 'No refunds' },
  { id: 'PARTIALLY_REFUNDED', label: 'Partially refunded' },
  { id: 'FULLY_REFUNDED', label: 'Fully refunded' }
];

export const DISPUTE_STATUS_OPTIONS = [
  { id: 'NOT_DISPUTED', label: 'Not disputed' },
  { id: 'DISPUTED', label: 'Disputed' }
];

export const REMINDER_STATUS_OPTIONS = [
  { id: 'NO_REMINDERS_SENT', label: 'No reminders sent' },
  { id: 'REMINDERS_SENT', label: 'Reminders sent' },
  { id: 'REMINDERS_DUE', label: 'Reminder due' }
];

export const JOB_STATUS_OPTIONS = [
  { id: 'NO_LINKED_JOB', label: 'No linked job' },
  { id: 'HAS_LINKED_JOB', label: 'Has linked job' },
  { id: 'JOB_COMPLETED', label: 'Job completed' }
];

export const ESTIMATE_STATUS_OPTIONS = [
  { id: 'NO_LINKED_ESTIMATE', label: 'No linked estimate' },
  { id: 'HAS_LINKED_ESTIMATE', label: 'Has linked estimate' }
];

export const TIP_COLLECTED_OPTIONS = [{ id: 'tip', label: 'Only with Tips' }];

export const INVOICE_STATUS_OPTIONS = [
  { id: 'PENDING', label: 'Pending' },
  { id: 'PARTIALLY_PAID', label: 'Partially paid' },
  { id: 'PARTIALLY_REFUNDED', label: 'Partially refunded' },
  { id: 'PAID', label: 'Paid' },
  { id: 'REFUNDED', label: 'Refunded' },
  { id: 'OVERDUE', label: 'Overdue' }
];

export const OPEN_INVOICE_STATUS_OPTIONS = [
  InvoiceStatus.PENDING,
  InvoiceStatus.PROCESSING,
  InvoiceStatus.PARTIALLY_PAID,
  InvoiceStatus.OVERDUE,
  InvoiceStatus.PARTIALLY_REFUNDED
];

export const PAID_INVOICE_STATUS_OPTIONS = [
  InvoiceStatus.PAID_QR,
  InvoiceStatus.PAID,
  InvoiceStatus.REFUNDED,
  InvoiceStatus.REFUND_INITIATED
];

export const INVOICE_MORE_FILTER_OPTIONS: FilterOption[] = [
  {
    id: InvoiceFilterIds.AMOUNT,
    label: 'Amount',
    type: FilterDropdownType.AMOUNT,
    isAmountFilter: true
  },
  {
    id: InvoiceFilterIds.PAYMENT_METHODS,
    label: 'Payment method',
    type: FilterDropdownType.DROPDOWN,
    idKey: 'id',
    nameKey: 'label',
    selectionType: FilterSelectionType.CHECKBOX,
    showSearchInFilter: false
  },
  {
    id: InvoiceFilterIds.TIP_COLLECTED,
    label: 'Tip collected',
    type: FilterDropdownType.DROPDOWN,
    idKey: 'id',
    nameKey: 'label',
    selectionType: FilterSelectionType.RADIO,
    showSearchInFilter: false
  },
  {
    id: InvoiceFilterIds.REFUND_STATUS,
    label: 'Refund status',
    type: FilterDropdownType.DROPDOWN,
    idKey: 'id',
    nameKey: 'label',
    selectionType: FilterSelectionType.RADIO,
    showSearchInFilter: false
  },
  {
    id: InvoiceFilterIds.DISPUTE_STATUS,
    label: 'Dispute status',
    type: FilterDropdownType.DROPDOWN,
    idKey: 'id',
    nameKey: 'label',
    selectionType: FilterSelectionType.RADIO,
    showSearchInFilter: false
  },
  {
    id: InvoiceFilterIds.REMINDER_STATUS,
    label: 'Reminder status',
    type: FilterDropdownType.DROPDOWN,
    idKey: 'id',
    nameKey: 'label',
    selectionType: FilterSelectionType.RADIO,
    showSearchInFilter: false
  },
  {
    id: InvoiceFilterIds.JOB_STATUS,
    label: 'Job status',
    type: FilterDropdownType.DROPDOWN,
    idKey: 'id',
    nameKey: 'label',
    selectionType: FilterSelectionType.RADIO,
    showSearchInFilter: false
  },
  {
    id: InvoiceFilterIds.ESTIMATE_STATUS,
    label: 'Estimate status',
    type: FilterDropdownType.DROPDOWN,
    idKey: 'id',
    nameKey: 'label',
    selectionType: FilterSelectionType.RADIO,
    showSearchInFilter: false
  }
];

export enum StatusIndicator {
  NO_PAYMENTS_YET = 'NO_PAYMENTS_YET',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  FULLY_PAID = 'FULLY_PAID'
}

export enum DISPUTE_REFUND_STATUS_INDICATOR {
  FULL = 'FULL',
  PARTIAL = 'PARTIAL',
  NONE = 'NONE'
}

export enum TransactionType {
  PAYMENT = 'PAYMENT',
  REFUND = 'REFUND',
  DISPUTE = 'DISPUTE',
  RETURN = 'RETURN'
}

export enum INVOICE_TAB_TITLE {
  ALL_INVOICES = 'All Invoices',
  OPEN = 'Open',
  PAST_DUE = 'Past Due',
  PAID = 'Paid',
  PENDING = 'Pending'
}

export enum INVOICE_TAB_STATUS {
  OPEN = 'open',
  PAST_DUE = 'past_due',
  PAID = 'paid',
  ALL_INVOICES = 'all'
}

export const TAB_HEADS = [
  {
    id: 1,
    title: INVOICE_TAB_TITLE.ALL_INVOICES,
    value: INVOICE_TAB_STATUS.ALL_INVOICES
  },
  {
    id: 2,
    title: INVOICE_TAB_TITLE.OPEN,
    value: INVOICE_TAB_STATUS.OPEN
  },
  {
    id: 3,
    title: INVOICE_TAB_TITLE.PAST_DUE,
    value: INVOICE_TAB_STATUS.PAST_DUE
  },
  {
    id: 4,
    title: INVOICE_TAB_TITLE.PAID,
    value: INVOICE_TAB_STATUS.PAID
  }
];

export enum TransactionStatus {
  SCANNED = 'SCANNED',
  INITIATED = 'INITIATED',
  GENERATED = 'GENERATED',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  CANCELLED = 'CANCELLED',
  TIMED_OUT = 'TIMED_OUT',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  BLOCKED = 'BLOCKED',
  REFUNDED = 'REFUNDED',
  REFUND_INITIATED = 'REFUND_INITIATED',
  REFUND_FAILED = 'REFUND_FAILED',
  REFUND_CANCELLED = 'REFUND_CANCELED',
  SCHEDULED = 'SCHEDULED',
  IN_TRANSIT = 'IN_TRANSIT',
  VOID = 'VOID'
}

export enum CreateInvoiceHeaderType {
  CREATE = 'Create',
  PREVIEW = 'Preview'
}
